/**
 * Administrative Unit Service
 * 
 * Provides hierarchical administrative unit lookup functions based on adminCode patterns.
 * Supports Zimbabwe's 4-level hierarchy: Country → Province → District → Ward
 */

import { apiClient } from './api';
import type { AdministrativeUnit } from '../types/administrativeUnit';

export interface HierarchyLevel {
  level: number;
  name: string;
  units: AdministrativeUnit[];
}

/**
 * Get parent adminCode from child adminCode
 * @param adminCode Child administrative code
 * @returns Parent adminCode or null if no parent
 */
export function getParentCode(adminCode: string): string | null {
  if (!adminCode || adminCode.length < 4) return null;
  
  // Remove last 2 digits to get parent code
  return adminCode.slice(0, -2);
}

/**
 * Get expected child code pattern from parent adminCode
 * @param adminCode Parent administrative code
 * @returns Pattern for child codes
 */
export function getChildCodePattern(adminCode: string): string {
  return `${adminCode}%`; // SQL LIKE pattern for children
}

/**
 * Get administrative unit by ID
 */
export async function getAdministrativeUnitById(id: string): Promise<AdministrativeUnit | null> {
  try {
    const response = await apiClient.getAdministrativeUnitById(id);
    return response.data || null;
  } catch (error) {
    console.error('Error fetching administrative unit:', error);
    return null;
  }
}

/**
 * Get administrative unit by adminCode
 */
export async function getAdministrativeUnitByCode(adminCode: string): Promise<AdministrativeUnit | null> {
  try {
    const response = await apiClient.getAdministrativeUnits({ adminCode });
    const units = response.administrativeUnits || [];
    return units.length > 0 ? units[0] : null;
  } catch (error) {
    console.error('Error fetching administrative unit by code:', error);
    return null;
  }
}

/**
 * Get parent administrative unit
 * @param adminCode Child administrative code
 * @returns Parent administrative unit or null
 */
export async function getParent(adminCode: string): Promise<AdministrativeUnit | null> {
  const parentCode = getParentCode(adminCode);
  if (!parentCode) return null;
  
  return await getAdministrativeUnitByCode(parentCode);
}

/**
 * Get children administrative units
 * @param adminCode Parent administrative code
 * @returns Array of child administrative units
 */
export async function getChildren(adminCode: string): Promise<AdministrativeUnit[]> {
  try {
    const response = await apiClient.getAdministrativeUnits({ parentCode: adminCode });
    return response.administrativeUnits || [];
  } catch (error) {
    console.error('Error fetching children administrative units:', error);
    return [];
  }
}

/**
 * Get administrative units by level
 * @param adminLevel Administrative level (0=Country, 1=Province, 2=District, 3=Ward)
 * @param countryCode Country code filter (default: 'ZW')
 * @returns Array of administrative units at the specified level
 */
export async function getAdministrativeUnitsByLevel(
  adminLevel: number, 
  countryCode: string = 'ZW'
): Promise<AdministrativeUnit[]> {
  try {
    const response = await apiClient.getAdministrativeUnits({ adminLevel, countryCode });
    return response.administrativeUnits || [];
  } catch (error) {
    console.error('Error fetching administrative units by level:', error);
    return [];
  }
}

/**
 * Get provinces (Level 1 administrative units)
 * @param countryCode Country code (default: 'ZW')
 * @returns Array of provinces
 */
export async function getProvinces(countryCode: string = 'ZW'): Promise<AdministrativeUnit[]> {
  return await getAdministrativeUnitsByLevel(1, countryCode);
}

/**
 * Get districts for a specific province
 * @param provinceCode Province administrative code (e.g., 'ZW10')
 * @returns Array of districts in the province
 */
export async function getDistrictsForProvince(provinceCode: string): Promise<AdministrativeUnit[]> {
  return await getChildren(provinceCode);
}

/**
 * Get wards for a specific district
 * @param districtCode District administrative code (e.g., 'ZW1021')
 * @returns Array of wards in the district
 */
export async function getWardsForDistrict(districtCode: string): Promise<AdministrativeUnit[]> {
  return await getChildren(districtCode);
}

/**
 * Get full hierarchy path for an administrative unit
 * @param adminCode Administrative code
 * @returns Array of administrative units from country to the specified unit
 */
export async function getHierarchyPath(adminCode: string): Promise<AdministrativeUnit[]> {
  const path: AdministrativeUnit[] = [];
  let currentCode: string | null = adminCode;
  
  // Build path from child to parent
  while (currentCode) {
    const unit = await getAdministrativeUnitByCode(currentCode);
    if (unit) {
      path.unshift(unit); // Add to beginning of array
    }
    currentCode = getParentCode(currentCode);
  }
  
  return path;
}

/**
 * Validate adminCode hierarchy consistency
 * @param adminCode Administrative code to validate
 * @returns Validation result with details
 */
export async function validateHierarchy(adminCode: string): Promise<{
  isValid: boolean;
  issues: string[];
  parentExists: boolean;
  childrenCount: number;
}> {
  const issues: string[] = [];
  let parentExists = false;
  let childrenCount = 0;
  
  try {
    // Check if unit exists
    const unit = await getAdministrativeUnitByCode(adminCode);
    if (!unit) {
      issues.push(`Administrative unit with code ${adminCode} not found`);
      return { isValid: false, issues, parentExists, childrenCount };
    }
    
    // Check parent exists (if not level 0)
    if (unit.adminLevel > 0) {
      const parent = await getParent(adminCode);
      parentExists = !!parent;
      if (!parent) {
        issues.push(`Parent not found for ${adminCode}`);
      }
    } else {
      parentExists = true; // Country level has no parent
    }
    
    // Check children exist (if not level 3)
    if (unit.adminLevel < 3) {
      const children = await getChildren(adminCode);
      childrenCount = children.length;
    }
    
    return {
      isValid: issues.length === 0,
      issues,
      parentExists,
      childrenCount
    };
    
  } catch (error) {
    issues.push(`Validation error: ${error instanceof Error ? error.message : String(error)}`);
    return { isValid: false, issues, parentExists, childrenCount };
  }
}